import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { getFirestore, collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { networkClubService } from '../../services/networkClubService';
import type { NetworkClub } from '../../types/networkclub';
import { generateClubSlug, clubCanonicalUrl, profileCanonicalUrl, truncateForMeta } from '../../utils/clubSlug';

const db = getFirestore();

// ─── Types ────────────────────────────────────────────────────────────────────

interface ClubMember {
  id: string;
  name: string;
  photoURL: string;
  role: 'owner' | 'organizer' | 'member' | 'guest';
  functionTitle?: string;
}

export interface ClubVerhaalPageProps {
  entityId: string;
  entityType: 'networkclub' | 'mastermind' | 'community';
  onBack?: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  networkclub: 'Netwerkclub',
  mastermind:  'Mastermind',
  community:   'Community',
};

const DEFAULT_COLORS: Record<string, { primary: string; secondary: string; accent: string }> = {
  networkclub: { primary: '#2D5E4F', secondary: '#1B3A4B', accent: '#7A9E8E' },
  mastermind:  { primary: '#1B3A4B', secondary: '#2D5E4F', accent: '#5B8FA8' },
  community:   { primary: '#5B4FCF', secondary: '#1B3A4B', accent: '#8B7FE8' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const hex2rgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

const withAlpha = (hex: string, alpha: number): string => {
  try {
    const [r, g, b] = hex2rgb(hex);
    return `rgba(${r},${g},${b},${alpha})`;
  } catch { return hex; }
};

// ─── Component ────────────────────────────────────────────────────────────────

const ClubVerhaalPage: React.FC<ClubVerhaalPageProps> = ({ entityId, entityType, onBack }) => {
  const [club, setClub] = useState<(NetworkClub & Record<string, any>) | null>(null);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [coverPosition, setCoverPosition] = useState({ x: 50, y: 50 });

  // ── Data loading ───────────────────────────────────────────────────────────
  const loadClub = useCallback(async () => {
    try {
      setLoading(true);
      const clubData = await networkClubService.getNetworkClubByEntityId(entityId);
      if (!clubData) { setLoading(false); return; }

      const entitySnap = await getDoc(doc(db, 'entities', entityId));
      const entityData: any = entitySnap.exists() ? entitySnap.data() : {};

      const merged: any = {
        ...clubData,
        primaryColor:   (clubData as any).primaryColor   ?? entityData.primaryColor,
        secondaryColor: (clubData as any).secondaryColor ?? entityData.secondaryColor,
        accentColor:    (clubData as any).accentColor    ?? entityData.accentColor,
        coverImageUrl:  (clubData as any).coverImageUrl  || entityData.coverImageUrl || '',
        coverPosition:  (clubData as any).coverPosition  || entityData.coverPosition || { x: 50, y: 50 },
        logoUrl:        (clubData as any).logoUrl        || entityData.logoUrl || '',
        description:    (clubData as any).description    || entityData.description || '',
        values:         (clubData as any).values         || entityData.values || [],
        slug:           (clubData as any).slug           || entityData.slug || generateClubSlug(clubData.name),
      };
      setClub(merged);
      setCoverImageUrl(merged.coverImageUrl);
      setCoverPosition(merged.coverPosition);

      const membersQuery = query(collection(db, 'entityMembers'), where('entityId', '==', entityId));
      const membersSnap = await getDocs(membersQuery);
      const membersData: ClubMember[] = [];

      await Promise.all(
        membersSnap.docs.map(async (memberDoc) => {
          const md = memberDoc.data();
          const [userSnap, profileSnap] = await Promise.all([
            getDoc(doc(db, 'users', md.userId)),
            getDoc(doc(db, 'userProfiles', md.userId)),
          ]);
          const u: any = userSnap.exists() ? userSnap.data() : {};
          const p: any = profileSnap.exists() ? profileSnap.data() : {};
          const name = (u.displayName || p.displayName || u.name || p.name || md.displayName || 'Lid').toString().trim();
          membersData.push({
            id: md.userId,
            name,
            photoURL: (u.photoURL || p.photoURL || md.photoURL || '').toString(),
            role: md.role || 'member',
            functionTitle: (p.role || u.role || '').toString() || undefined,
          });
        })
      );

      const roleOrder: Record<string, number> = { owner: 0, organizer: 1, member: 2, guest: 3 };
      membersData.sort((a, b) => (roleOrder[a.role] ?? 3) - (roleOrder[b.role] ?? 3));
      setMembers(membersData);
    } catch (err) {
      console.error('ClubVerhaalPage load error:', err);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { loadClub(); }, [loadClub]);

  // ── Loading / error states ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F9FAFB' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #E5E7EB', borderTopColor: '#2D5E4F', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: '#9CA3AF', fontSize: 13 }}>Verhaal laden…</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!club) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F9FAFB' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#6B7280', marginBottom: 16 }}>Club niet gevonden.</p>
          {onBack && (
            <button onClick={onBack} style={{ padding: '8px 16px', borderRadius: 9, border: '1.5px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>← Terug</button>
          )}
        </div>
      </div>
    );
  }

  // ── Derived values ─────────────────────────────────────────────────────────
  const def = DEFAULT_COLORS[entityType] || DEFAULT_COLORS.networkclub;
  const C = {
    primary:   club.primaryColor   || def.primary,
    secondary: club.secondaryColor || def.secondary,
    accent:    club.accentColor    || def.accent,
  };

  const typeLabel   = TYPE_LABELS[entityType] || 'Club';
  const founder     = members.find(m => m.role === 'owner') || members.find(m => m.role === 'organizer');
  const categories: string[] = Array.isArray(club.categories) ? club.categories : [];
  const clubValues: string[]  = Array.isArray(club.values) ? club.values : categories.slice(0, 3);
  const description: string  = club.description || '';
  const slug        = club.slug || generateClubSlug(club.name);
  const canonicalUrl = clubCanonicalUrl(slug);
  const isPrivate   = club.visibility === 'private';
  const metaDesc    = truncateForMeta(description || `${typeLabel} op SuperKonnected — ${club.name}`);
  const coverForOG  = coverImageUrl || club.logoUrl || '';

  // ── SEO helpers ────────────────────────────────────────────────────────────
  const jsonLd: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: club.name,
    description: description || undefined,
    url: canonicalUrl,
    logo: club.logoUrl || undefined,
    image: coverForOG || undefined,
    foundingDate: club.foundedYear ? String(club.foundedYear) : undefined,
    ...(founder ? {
      founder: {
        '@type': 'Person',
        name: founder.name,
        url: profileCanonicalUrl(founder.id),
      },
    } : {}),
    memberOf: { '@type': 'WebSite', name: 'SuperKonnected', url: 'https://superkonnected.com' },
    ...(club.memberCount != null ? {
      numberOfEmployees: { '@type': 'QuantitativeValue', value: club.memberCount },
    } : {}),
    ...(clubValues.length > 0 ? { knowsAbout: clubValues } : {}),
    location: { '@type': 'VirtualLocation', url: canonicalUrl },
    ...(club.city ? {
      address: { '@type': 'PostalAddress', addressLocality: club.city, addressCountry: club.country || 'NL' },
    } : {}),
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>

      {/* ── SEO / GEO HEAD ── */}
      <Helmet>
        {isPrivate
          ? <meta name="robots" content="noindex, nofollow" />
          : <meta name="robots" content="index, follow" />
        }
        <title>{club.name} — {typeLabel} op SuperKonnected</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title"       content={`${club.name} — ${typeLabel} op SuperKonnected`} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type"        content="website" />
        <meta property="og:url"         content={canonicalUrl} />
        {coverForOG && <meta property="og:image" content={coverForOG} />}

        {/* JSON-LD structured data */}
        {!isPrivate && (
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        )}
      </Helmet>

      {/* ── STICKY TERUGKNOP BAR ── */}
      {onBack && (
        <div style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(8px)', borderBottom: '1px solid #F3F4F6', padding: '8px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button type="button" onClick={onBack}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 9, border: '1.5px solid #E5E7EB', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            ← Terug naar de club
          </button>
          <span style={{ fontSize: 13, color: '#9CA3AF', fontWeight: 500 }}>Ons verhaal van <strong style={{ color: '#374151' }}>{club.name}</strong></span>
        </div>
      )}

      {/* ── HERO ── */}
      <div style={{ position: 'relative', minHeight: 300, background: `linear-gradient(135deg, ${C.secondary} 0%, ${C.primary} 55%, ${C.accent} 100%)`, overflow: 'hidden' }}>
        {coverImageUrl && (
          <img src={coverImageUrl} alt="" aria-hidden="true"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${coverPosition.x}% ${coverPosition.y}%`, opacity: 0.4 }} />
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.18) 60%, transparent 100%)' }} />

        {/* Club logo */}
        <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 24px 80px' }}>
          <div style={{ width: 96, height: 96, borderRadius: '50%', border: '4px solid rgba(255,255,255,0.9)', background: '#fff', boxShadow: '0 6px 24px rgba(0,0,0,0.22)', overflow: 'hidden', flexShrink: 0, marginBottom: 20 }}>
            {club.logoUrl ? (
              <img src={club.logoUrl} alt={`Logo ${club.name}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 36, fontWeight: 900, color: '#fff' }}>{club.name.charAt(0).toUpperCase()}</span>
              </div>
            )}
          </div>

          {/* h1 — visible for GEO/SEO */}
          <h1 style={{ fontSize: 30, fontWeight: 900, color: '#fff', margin: '0 0 8px', textAlign: 'center', lineHeight: 1.15, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
            {club.name}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 20 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', background: 'rgba(255,255,255,0.18)', color: '#fff', borderRadius: 20, padding: '3px 12px', fontSize: 12.5, fontWeight: 700, backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.25)' }}>
              {typeLabel}
            </span>
            {categories.slice(0, 2).map((cat, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', borderRadius: 20, padding: '3px 10px', fontSize: 11.5, fontWeight: 600, backdropFilter: 'blur(4px)' }}>
                {cat}
              </span>
            ))}
            {(club.city || club.region) && (
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                📍 {[club.city, club.region].filter(Boolean).join(', ')}
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            {club.website && (
              <button type="button"
                onClick={() => { const url = club.website!; window.open(url.startsWith('http') ? url : `https://${url}`, '_blank', 'noopener,noreferrer'); }}
                style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: '#fff', color: C.primary, fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                🌐 Website
              </button>
            )}
            {club.linkedin && (
              <button type="button"
                onClick={() => window.open(club.linkedin, '_blank', 'noopener,noreferrer')}
                style={{ padding: '9px 18px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.5)', background: 'transparent', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                LinkedIn
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 20px 64px', display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* ONS VERHAAL SECTIE */}
        <section aria-labelledby="ons-verhaal-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <div style={{ width: 4, height: 28, borderRadius: 4, background: `linear-gradient(to bottom, ${C.primary}, ${C.accent})`, flexShrink: 0 }} />
            <h2 id="ons-verhaal-title" style={{ fontSize: 20, fontWeight: 900, color: '#111827', margin: 0 }}>Ons verhaal</h2>
          </div>

          {/* Waarden — 3 cards (GEO: leesbaar als tekst) */}
          {clubValues.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
              {clubValues.slice(0, 3).map((val, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', border: `1px solid ${withAlpha(C.primary, 0.12)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>Waarde {i + 1}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{val}</div>
                </div>
              ))}
            </div>
          )}

          {/* Club beschrijving — doorlopende tekst (GEO: indexed as body text) */}
          <div style={{ background: '#fff', borderRadius: 14, padding: '24px 26px', border: `1px solid ${withAlpha(C.primary, 0.1)}`, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
            {description ? (
              <p style={{ fontSize: 15.5, color: '#374151', lineHeight: 1.85, margin: 0, whiteSpace: 'pre-wrap' }}>{description}</p>
            ) : (
              <p style={{ fontSize: 14, color: '#9CA3AF', fontStyle: 'italic', margin: 0, lineHeight: 1.7 }}>
                Dit {typeLabel.toLowerCase()} heeft nog geen verhaal toegevoegd.
              </p>
            )}
          </div>
        </section>

        {/* FOCUSGEBIEDEN */}
        {categories.length > 0 && (
          <section aria-labelledby="focusgebieden-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 4, height: 28, borderRadius: 4, background: `linear-gradient(to bottom, ${C.primary}, ${C.accent})`, flexShrink: 0 }} />
              <h2 id="focusgebieden-title" style={{ fontSize: 20, fontWeight: 900, color: '#111827', margin: 0 }}>Focusgebieden</h2>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {categories.map((cat, i) => (
                <span key={i} style={{ display: 'inline-block', padding: '6px 14px', borderRadius: 20, background: withAlpha(C.primary, 0.08), color: C.primary, fontSize: 13, fontWeight: 600, border: `1px solid ${withAlpha(C.primary, 0.15)}` }}>
                  {cat}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* ONTMOET DE FOUNDER — h2 voor GEO */}
        {founder && (
          <section aria-labelledby="founder-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <div style={{ width: 4, height: 28, borderRadius: 4, background: `linear-gradient(to bottom, ${C.primary}, ${C.accent})`, flexShrink: 0 }} />
              <h2 id="founder-title" style={{ fontSize: 20, fontWeight: 900, color: '#111827', margin: 0 }}>Ontmoet de Founder</h2>
            </div>
            <div style={{ background: '#fff', borderRadius: 14, padding: '22px 24px', border: `1px solid ${withAlpha(C.primary, 0.1)}`, boxShadow: '0 1px 6px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: `3px solid ${withAlpha(C.primary, 0.18)}`, background: '#F3F4F6' }}>
                {founder.photoURL ? (
                  <img src={founder.photoURL} alt={`Foto van ${founder.name}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: withAlpha(C.primary, 0.12), color: C.primary, fontSize: 26, fontWeight: 800 }}>
                    {founder.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Name in a visible tag — GEO-readable */}
                <p style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 3px' }}>{founder.name}</p>
                {founder.functionTitle && (
                  <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 12px' }}>{founder.functionTitle}</p>
                )}
                <button type="button"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('openUserProfile', { detail: { userId: founder.id } }));
                    window.location.hash = '#mijn-verhaal';
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, border: `1.5px solid ${withAlpha(C.primary, 0.3)}`, background: 'transparent', color: C.primary, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  Bekijk persoonlijk verhaal →
                </button>
              </div>
            </div>
          </section>
        )}

        {/* CLUB DETAILS */}
        <section aria-labelledby="club-details-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ width: 4, height: 28, borderRadius: 4, background: `linear-gradient(to bottom, ${C.primary}, ${C.accent})`, flexShrink: 0 }} />
            <h2 id="club-details-title" style={{ fontSize: 20, fontWeight: 900, color: '#111827', margin: 0 }}>Over de club</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10 }}>
            {([
              { label: 'Type',          value: typeLabel },
              club.memberCount != null && { label: 'Leden',         value: `${club.memberCount} leden` },
              club.meetingFrequency    && { label: 'Vergadering',    value: club.meetingFrequency },
              club.foundedYear         && { label: 'Opgericht',      value: `${club.foundedYear}` },
              (club.city || club.region) && { label: 'Locatie', value: [club.city, club.region].filter(Boolean).join(', ') },
              club.country             && { label: 'Land',           value: club.country },
              club.visibility          && { label: 'Toegang',        value: club.visibility === 'public' ? 'Open groep' : 'Besloten groep' },
            ] as any[]).filter(Boolean).map((item, i) => (
              <div key={i} style={{ padding: '12px 14px', borderRadius: 10, background: '#fff', border: `1px solid ${withAlpha(C.primary, 0.08)}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </section>

        {/* LEDEN */}
        {members.filter(m => m.role !== 'guest').length > 0 && (
          <section aria-labelledby="leden-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{ width: 4, height: 28, borderRadius: 4, background: `linear-gradient(to bottom, ${C.primary}, ${C.accent})`, flexShrink: 0 }} />
              <h2 id="leden-title" style={{ fontSize: 20, fontWeight: 900, color: '#111827', margin: 0 }}>Onze leden</h2>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>{members.filter(m => m.role !== 'guest').length} leden</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {members.filter(m => m.role !== 'guest').slice(0, 16).map(member => (
                <button key={member.id} type="button"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('openUserProfile', { detail: { userId: member.id } }));
                    window.location.hash = '#mijn-verhaal';
                  }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, padding: '12px 14px', borderRadius: 12, border: `1px solid ${withAlpha(C.primary, 0.1)}`, background: '#fff', cursor: 'pointer', minWidth: 78, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'box-shadow 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)')}
                  onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)')}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', border: `2px solid ${withAlpha(C.primary, 0.15)}`, background: '#F3F4F6' }}>
                    {member.photoURL ? (
                      <img src={member.photoURL} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: withAlpha(C.primary, 0.1), color: C.primary, fontSize: 16, fontWeight: 800 }}>
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', textAlign: 'center', maxWidth: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {member.name.split(' ')[0]}
                  </div>
                  {(member.role === 'owner' || member.role === 'organizer') && (
                    <span style={{ fontSize: 9, fontWeight: 700, background: withAlpha(C.primary, 0.1), color: C.primary, borderRadius: 6, padding: '1px 5px' }}>
                      {member.role === 'owner' ? 'Founder' : 'Org.'}
                    </span>
                  )}
                </button>
              ))}
              {members.filter(m => m.role !== 'guest').length > 16 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px 14px', borderRadius: 12, border: `1px dashed ${withAlpha(C.primary, 0.2)}`, minWidth: 78, color: '#9CA3AF', fontSize: 12, fontWeight: 600, background: '#fff' }}>
                  +{members.filter(m => m.role !== 'guest').length - 16}
                </div>
              )}
            </div>
          </section>
        )}

      </div>
    </div>
  );
};

export default ClubVerhaalPage;
